import "./index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { darkTheme } from "./theme";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { ShellLayout } from "./layout/ShellLayout";
import { HomePage } from "./pages/HomePage";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { MagicLinkSent } from "./pages/MagicLinkSent";
import { MagicLinkVerify } from "./pages/MagicLinkVerify";
import { InvitationAccept } from "./pages/InvitationAccept";
import { EmailChangeConfirm } from "./pages/EmailChangeConfirm";
import { PageServiceInstances } from "./pages/PageServiceInstances.tsx";
import { PageHealthChecks } from "./pages/PageHealthChecks.tsx";
import { PageIngressTokens } from "./pages/PageIngressTokens.tsx";
import { PageTenantSettings } from "./pages/PageTenantSettings.tsx";
import { PageMetricsQuery } from "./pages/PageMetricsQuery";
import { PageTracesQuery } from "./pages/PageTracesQuery";
import { PageSpansQuery } from "./pages/PageSpansQuery";
import { PageSpanPerformance } from "./pages/PageSpanPerformance";
import { PageSingleSpanDetail } from "./pages/PageSingleSpanDetail";
import { PageTraceDetail } from "./pages/PageTraceDetail";
import { PageLogsQuery } from "./pages/PageLogsQuery";
import { PageLogDetail } from "./pages/PageLogDetail";
import { PageTenant } from "./pages/PageTenant.tsx";
import { PageProduct } from "./pages/PageProduct.tsx";
import { PageSettings } from "./pages/PageSettings";
import { PageTenantNew } from "./pages/PageTenantNew.tsx";
import { PageProductNew } from "./pages/PageProductNew.tsx";
import { PageServiceNew } from "./pages/PageServiceNew.tsx";
import { PageIngressTokenNew } from "./pages/PageIngressTokenNew.tsx";
import { PageServiceHealth } from "./pages/PageServiceHealth.tsx";
import { PageServiceHealthDetail } from "./pages/PageServiceHealthDetail.tsx";
import { PageHealthCheckNew } from "./pages/PageHealthCheckNew.tsx";
import { PageHealthCheckEdit } from "./pages/PageHealthCheckEdit.tsx";
import { PageServiceAlerts } from "./pages/PageServiceAlerts.tsx";
import { PageAlertNew } from "./pages/PageAlertNew.tsx";
import { PageAlertEdit } from "./pages/PageAlertEdit.tsx";
import { PageAlertChannelNew } from "./pages/PageAlertChannelNew.tsx";
import { PageAlertChannelEdit } from "./pages/PageAlertChannelEdit.tsx";

export function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="auth/magic-link/sent" element={<MagicLinkSent />} />
          <Route path="auth/magic-link/verify/:token" element={<MagicLinkVerify />} />
          <Route
            path="auth/email-change/confirm-old/:token"
            element={<EmailChangeConfirm kind="confirm-old" />}
          />
          <Route
            path="auth/email-change/confirm-new/:token"
            element={<EmailChangeConfirm kind="confirm-new" />}
          />
          <Route path="invitation/:token" element={<InvitationAccept />} />

          <Route
            element={
              <ProtectedRoute>
                <ShellLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/home" replace />} />
            <Route path="home" element={<HomePage />} />

            <Route path="tenants/new" element={<PageTenantNew />} />
            <Route path="tenants/:tenant" element={<Navigate to="products" replace />} />
            <Route path="tenants/:tenant/products" element={<PageTenant />} />
            <Route path="tenants/:tenant/products/new" element={<PageProductNew />} />
            <Route path="tenants/:tenant/settings" element={<PageTenantSettings />} />
            <Route path="products/:product" element={<Navigate to="services" replace />} />
            <Route path="products/:product/services" element={<PageProduct />} />
            <Route path="products/:product/services/new" element={<PageServiceNew />} />
            <Route path="settings" element={<PageSettings />} />

            <Route path="services/:service" element={<Navigate to="health" replace />} />
            <Route path="services/:service/health" element={<PageServiceHealth />} />
            <Route
              path="services/:service/health/res/:responseId"
              element={<PageServiceHealthDetail />}
            />
            <Route path="services/:service/instances" element={<PageServiceInstances />} />
            <Route path="services/:service/health-checks" element={<PageHealthChecks />} />
            <Route path="services/:service/ingress-tokens" element={<PageIngressTokens />} />
            <Route path="services/:service/ingress-tokens/new" element={<PageIngressTokenNew />} />
            <Route path="services/:service/health-checks/new" element={<PageHealthCheckNew />} />
            <Route
              path="services/:service/health-checks/:healthCheckId"
              element={<PageHealthCheckEdit />}
            />
            <Route path="services/:service/alerts" element={<PageServiceAlerts />} />
            <Route path="services/:service/alerts/new" element={<PageAlertNew />} />
            <Route path="services/:service/alerts/:alertId" element={<PageAlertEdit />} />
            <Route path="services/:service/channels/new" element={<PageAlertChannelNew />} />
            <Route
              path="services/:service/channels/:channelId"
              element={<PageAlertChannelEdit />}
            />
            <Route path="services/:service/metrics" element={<PageMetricsQuery />} />
            <Route path="services/:service/traces" element={<PageTracesQuery />} />
            <Route path="services/:service/spans" element={<PageSpansQuery />} />
            <Route
              path="services/:service/spans/perf/:spanName"
              element={<PageSpanPerformance />}
            />
            <Route path="services/:service/spans/:spanId" element={<PageSingleSpanDetail />} />
            <Route path="services/:service/logs" element={<PageLogsQuery />} />
            <Route path="services/:service/logs/:logId" element={<PageLogDetail />} />
            <Route path="services/:service/traces/:traceId" element={<PageTraceDetail />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
